import React, { useEffect, useState } from "react";
import axios from "axios";
import { Button, Grid, Group, Title, Divider, Text, Flex } from "@mantine/core";
import { shallow } from "zustand/shallow";
import { useTranslation } from "react-i18next";

import EffortPickerDeux from "./EffortPickerDeux";
import EffortRegistered from "./EffortRegistered";
import EffortView from "./EffortView";
import LocationLocator from "./LocationLocator";
import PreferredLocation from "./PreferredLocation";
import UserCheck from "./UserCheck";
import UserLocation from "./UserLocation";
import UserView from "./UserView";
import ViewError from "./ViewError";
import ViewLoader from "./ViewLoader";

import useAppStore from "./microsite-store";
import { micrositeUserLookup } from "./userLookup";
import { useGeolocation } from "./useGeolocation";
import { getGeolocation } from "./geoInterceptor";

import { getPlacementImage } from "@components/Asset/helpers";
import { placementDefs } from "@components/Asset/placementDefs";
import sendActionEvents, {
  actionIds,
} from "@components/EntityActionAssociator/sendActionEvents";

export default function Microsite({
  appConfigData,
  campaignId,
  entityInfoData,
  preview,
}) {
  const [error, setError] = useState(null);

  // Use geolocation hook
  const { locating, fetchGeolocation, setLocating } = useGeolocation();

  const { t, i18n } = useTranslation();

  const {
    appConfig,
    colors,
    coords,
    coordsNoLocation,
    email,
    entityInfo,
    location,
    phone,
    preferredLocation,
    appLoading,
    setAppLoading,
    setAppConfig,
    setCampaignId,
    setEntityInfo,
    setColors,
    setEmail,
    user,
    zip,
    showOtherEfforts,
    setShowOtherEfforts,
    registrationInfo,
    setRegistrationInfo,
  } = useAppStore(
    (state) => ({
      appConfig: state.appConfig,
      campaignId: state.campaignId,
      coords: state.coords,
      coordsNoLocation: state.coordsNoLocation,
      email: state.email,
      entityInfo: state.entityInfo,
      location: state.location,
      phone: state.phone,
      preferredLocation: state.preferredLocation,
      appLoading: state.appLoading,
      setAppConfig: state.setAppConfig,
      setCampaignId: state.setCampaignId,
      setEntityInfo: state.setEntityInfo,
      setColors: state.setColors,
      setCoords: state.setCoords,
      setEmail: state.setEmail,
      setEmailStreams: state.setEmailStreams,
      setLocation: state.setLocation,
      setPreferredLocation: state.setPreferredLocation,
      setUser: state.setUser,
      setAppLoading: state.setAppLoading,
      user: state.user,
      zip: state.zip,
      showOtherEfforts: state.showOtherEfforts,
      setShowOtherEfforts: state.setShowOtherEfforts,
      registrationInfo: state.registrationInfo,
      setRegistrationInfo: state.setRegistrationInfo,
    }),
    shallow
  );

  useEffect(() => {
    trackPageView();
  }, []);

  // Set up axios interceptor to fetch geolocation before API calls
  useEffect(() => {
    const interceptorId = axios.interceptors.request.use(
      async (config) => {
        // Only add geolocation to microsite API calls
        if (config.url && config.url.indexOf("microsite") > -1) {
          try {
            const coords = await getGeolocation();
            if (coords) {
              // Add coordinates to request data
              if (config.method === "post" || config.method === "put") {
                if (config.data) {
                  // If data is FormData, append to it
                  if (config.data instanceof FormData) {
                    config.data.append("latitude", coords.latitude);
                    config.data.append("longitude", coords.longitude);
                  } else if (typeof config.data === "object") {
                    // If data is an object, merge coordinates
                    config.data = {
                      ...config.data,
                      latitude: coords.latitude,
                      longitude: coords.longitude,
                    };
                  }
                } else {
                  // If no data exists, create new object
                  config.data = {
                    latitude: coords.latitude,
                    longitude: coords.longitude,
                  };
                }
              } else if (config.method === "get") {
                // For GET requests, add as query params
                config.params = {
                  ...config.params,
                  latitude: coords.latitude,
                  longitude: coords.longitude,
                };
              }
            }
          } catch (error) {
            // Silently fail - don't block the request if geolocation fails
            console.warn("Geolocation fetch failed:", error);
          }
        }
        return config;
      },
      (error) => {
        return Promise.reject(error);
      }
    );

    // Cleanup interceptor on unmount
    return () => {
      axios.interceptors.request.eject(interceptorId);
    };
  }, []);

  useEffect(() => {
    document.body.style.backgroundColor = "#f8f9fa";
    setEntityInfo(entityInfoData);
    setCampaignId(campaignId);
    setAppConfig(appConfigData);

    if (
      entityInfoData &&
      entityInfoData.language
      // entityInfoData.language !== "en"
    ) {
      i18n.changeLanguage(entityInfoData.language);
    }

    if (preview) {
      setEmail("example@example.com");
    }
  }, []);

  useEffect(() => {
    if (user) {
      fetchUser();
    }
  }, []);

  useEffect(() => {
    if (user) return;
    fetchGeolocation(entityInfoData);
  }, [JSON.stringify(appConfig), entityInfoData]);

  useEffect(() => {
    if (!appConfig) return;
    const backgroundImage = getPlacementImage(
      appConfig,
      entityInfo.assets,
      placementDefs.background
    );

    if (backgroundImage) {
      document.body.style.background = `url(${backgroundImage}) no-repeat center center`;
      document.body.style.backgroundSize = "cover";
      document.body.style.backgroundAttachment = "fixed";
    } else {
      document.body.style.background = "var(--mantine-color-dark-9)";
      document.body.style.backgroundSize = "initial";
      document.body.style.backgroundAttachment = "initial";
    }

    if (appConfig.colors) {
      setColors({ ...colors, ...appConfig.colors });

      document.body.style.backgroundColor =
        appConfig.colors.background || "#f8f9fa";
    }
  }, [JSON.stringify(entityInfo)]);

  function trackPageView() {
    sendActionEvents({
      events: entityInfo?.paid_media_settings?.events || [],
      action_id: actionIds.PAGE_VIEW,
    });
  }

  function fetchUser(calledFromEntryForm = false) {
    setLocating(true);
    setAppLoading(true);

    const userInfo = {};
    if (email) userInfo.email = email;
    if (phone) userInfo.mobile_phone = phone;

    micrositeUserLookup({
      userInfo,
      onRegistration: (user) => {
        setRegistrationInfo(user);
        setLocating(false);
      },
    })
      .then((usr) => {
        if (usr && !usr.date_submitted) {
          if (calledFromEntryForm) {
            clearEffortState();
          }
        }
      })
      .catch((err) => {
        // setUser(null);
        setError(err);
      })
      .finally(() => {
        setLocating(false);
        setAppLoading(false);
      });
  }

  function clearEffortState() {
    setLocating(false);
  }

  function onUserEnter() {
    fetchUser(true);
  }

  if (
    !entityInfo ||
    (!campaignId && !entityInfo?.campaign_effort_id) ||
    !appConfig
  )
    return null;

  // const showPreferredLocation = entityInfo.location
  //   ? false
  //   : entityInfo.preferred_location;

  const showPreferredLocation = entityInfo.preferred_location;

  const headerLogo = getPlacementImage(
    appConfig,
    entityInfo.assets,
    placementDefs.header_logo
  );

  const userHasEnteredEffort =
    user &&
    user.campaign_efforts &&
    user.campaign_efforts.find(
      (f) => f.id === entityInfo.campaign_effort_id && f.entered
    )
      ? true
      : false;

  if (locating || appLoading) {
    return <ViewLoader />;
  }

  if (error) {
    return (
      <div>
        <HeaderLogo logoUrl={headerLogo} />
        <ViewError
          title={t("microsite:generic_error_title")}
          order={1}
          subtitle={error}
          marginBottom={0}
        />
        {entityInfo && (
          <Flex
            justify="center"
            align="center"
            gap={1}
            direction="column"
            mt="lg"
          >
            <Text c="dimmed" size="xs" fw={600}>
              {entityInfo.title}
            </Text>
            {entityInfo.location && (
              <Text c="dimmed" size="xs">
                {entityInfo.location.name}
              </Text>
            )}
          </Flex>
        )}
      </div>
    );
  }

  if (showOtherEfforts) {
    return (
      <div>
        <EffortPickerDeux
          reqData={{
            campaign_effort_id: entityInfo.campaign_effort_id,
            user_id: user.id,
            location_id: location?.id,
          }}
          location={location}
        />
        {entityInfo.campaign_effort_id && (
          <>
            <Group justify="center">
              <Button
                variant="subtle"
                color="gray"
                style={{ marginTop: user ? "1em" : "3em" }}
                onClick={() => {
                  setShowOtherEfforts(false);
                }}
              >
                {t("microsite:back")}
              </Button>
            </Group>
          </>
        )}
      </div>
    );
  }

  if (registrationInfo && entityInfo.recurring_engagement === 2) {
    return (
      <>
        <HeaderLogo logoUrl={headerLogo} />
        <EffortRegistered
          registrationInfo={registrationInfo}
          info={entityInfo}
        />
        {entityInfo.campaign_effort_id && (
          <>
            <Group justify="center" mt="md">
              <Button
                radius="xl"
                onClick={() => {
                  setShowOtherEfforts(true);
                }}
              >
                {t("microsite:user_select_different_campaign_button")}
              </Button>
            </Group>
          </>
        )}
      </>
    );
  }

  if (!entityInfoData.location_id && !location) {
    if ((zip || coords) && !coordsNoLocation) {
      return (
        <>
          <HeaderLogo logoUrl={headerLogo} />
          <LocationLocator buttonText={t("microsite:register_here_button")} />
        </>
      );
    }

    return (
      <>
        <HeaderLogo logoUrl={headerLogo} />
        <UserLocation />
      </>
    );
  }

  if (!email && !phone && !user) {
    return (
      <>
        <HeaderLogo logoUrl={headerLogo} />
        <UserCheck />
      </>
    );
  }

  if (user && userHasEnteredEffort) {
    if (location && showPreferredLocation && !preferredLocation) {
      return (
        <>
          <PreferredLocation />
          {/* <Flex justify="center" mt="md">
            <Button onClick={() => showPreferredLocation(false)}>Back</Button>
          </Flex> */}
        </>
      );
    }

    return (
      <>
        <HeaderLogo logoUrl={headerLogo} />
        <UserView fetchUser={fetchUser} />
      </>
    );
  }

  if (entityInfo.campaign_effort_id) {
    if (!location) {
      return <UserLocation />;
    }

    if (preview) {
      return (
        <div>
          <Title order={3} c="white" mb="sm">
            Preview Entry View
          </Title>
          <HeaderLogo logoUrl={headerLogo} />
          <EffortView
            onEnterSuccess={(usr) => onUserEnter()}
            onMoreEfforts={() => {
              true;
            }}
            preview
          />
        </div>
      );
    }

    return (
      <>
        <HeaderLogo logoUrl={headerLogo} />
        <EffortView
          onEnterSuccess={(usr) => onUserEnter()}
          onMoreEfforts={() => {
            true;
          }}
        />
      </>
    );
  }

  return (
    <>
      <HeaderLogo logoUrl={headerLogo} />
      {((!coords && !zip && !location) || coordsNoLocation) && (
        <React.Fragment>
          <UserLocation />
        </React.Fragment>
      )}
      {(zip || coords) &&
        !coordsNoLocation &&
        !location &&
        !entityInfo.campaign_effort_id && (
          <LocationLocator buttonText={t("microsite:register_here_button")} />
        )}
      {location && !entityInfo.campaign_effort_id && (
        <React.Fragment>
          <EffortPickerDeux
            reqData={{
              campaign_effort_id: entityInfo.campaign_effort_id,
              user_id: user.id,
              location_id: location?.id,
            }}
            location={location}
          />
        </React.Fragment>
      )}
    </>
  );
}

const HeaderLogo = ({ logoUrl }) => {
  if (!logoUrl) return null;

  return (
    <Flex justify="center" mb="md">
      <img
        src={logoUrl}
        style={{ width: "125px", maxHeight: "100px", objectFit: "contain" }}
        alt="Logo"
      />
    </Flex>
  );
};
